// src/app/app.component.ts
import type { OnInit, OnDestroy} from '@angular/core';
import {
  Component, ChangeDetectionStrategy,
  inject, signal, PLATFORM_ID
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { RouterOutlet } from '@angular/router';
import { TenantService } from './core/tenant/tenant.service';
import { ThemeService } from './core/theme/theme.service';
import { SessionService } from './core/auth/session.service';
import { AuthService } from './core/auth/auth.service';
import { TranslatePipe } from './core/i18n/translate.pipe';
import { SessionTimeoutModalComponent } from './shared/components/session-timeout-modal/session-timeout-modal.component';

@Component({
  selector: 'app-root',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterOutlet, TranslatePipe, SessionTimeoutModalComponent],
  template: `
    @if (isFramed()) {
      <!-- Anti-clickjacking: si la app está dentro de un iframe, mostrar solo advertencia -->
      <div class="frame-warning" role="alert">
        <p>{{ 'common.security.frameWarning' | translate }}</p>
      </div>
    } @else {
      <!-- Banner de sin conexión (no intrusivo, en la parte superior) -->
      <!-- aria-live="assertive" para anunciar inmediatamente la pérdida de conexión -->
      <div
        class="offline-banner"
        [class.offline-banner--visible]="isOffline()"
        role="status"
        aria-live="assertive"
        aria-atomic="true"
        [attr.aria-hidden]="!isOffline()">
        <span aria-hidden="true">⚠</span>
        {{ 'common.errors.offline' | translate }}
      </div>

      <!-- Punto de montaje del router -->
      <!-- tabindex="-1" permite recibir foco desde el skip link -->
      <main id="main-content" tabindex="-1">
        <router-outlet />
      </main>

      <!-- Modal de timeout de sesión (siempre montado, visible solo cuando showWarning=true) -->
      <app-session-timeout-modal />
    }
  `,
  styles: [`
    :host { display: block; min-height: 100vh; }

    .frame-warning {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      padding: 24px;
      text-align: center;
      background: var(--color-bg);
      color: var(--color-text);
      font-size: var(--font-size-md);
    }

    /* El banner offline siempre está en el DOM (para aria-live)
       pero visualmente se muestra/oculta con CSS */
    .offline-banner {
      position: fixed;
      top: -60px;
      left: 0;
      right: 0;
      z-index: var(--z-toast);
      background: var(--color-warning);
      color: var(--text-on-warning);
      text-align: center;
      padding: var(--space-3) var(--space-4);
      font-size: var(--font-size-base);
      font-weight: var(--font-weight-medium);
      display: flex;
      align-items: center;
      justify-content: center;
      gap: var(--space-2);
      transition: top var(--transition-normal);
    }
    .offline-banner--visible { top: 0; }

    #main-content { outline: none; }
  `],
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly tenantService  = inject(TenantService);
  // Se inyecta sin usarse directamente para forzar su construcción temprana:
  // arranca la resolución del tema y el listener de matchMedia desde el
  // primer render de la app (incluida la pantalla de login), no solo cuando
  // el topbar con el botón de tema llega a montarse.
  private readonly themeService   = inject(ThemeService);
  private readonly sessionService = inject(SessionService);
  private readonly authService    = inject(AuthService);
  private readonly platformId     = inject(PLATFORM_ID);

  /** true si la app está cargada dentro de un iframe (clickjacking) */
  readonly isFramed  = signal(false);

  /** true cuando el navegador pierde la conexión a internet */
  readonly isOffline = signal(false);

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    // ── Anti-clickjacking ──────────────────────────────────────────────────
    // Si window.self !== window.top, la app está dentro de un iframe.
    // Mostrar advertencia y no renderizar el contenido de la aplicación.
    if (window.self !== window.top) {
      this.isFramed.set(true);
      return; // Detener la inicialización — no configurar nada más
    }

    // ── Eliminar loading screen pre-Angular ────────────────────────────────
    // El APP_INITIALIZER ya completó (config + traducciones cargadas).
    // Eliminar el spinner que estaba visible durante el bootstrap.
    document.getElementById('app-loading')?.remove();

    // ── Aplicar tema de la institución ────────────────────────────────────
    // Aplica primaryColor/primaryColorDark del runtime-config.json como CSS
    // custom property. Idempotente: ThemeService ya la invocó al construirse
    // (arriba, vía inject(ThemeService)) con el tema correcto; esta llamada
    // es una red de seguridad, no la primera aplicación real.
    this.tenantService.applyTheme();

    // ── Detección de conectividad ──────────────────────────────────────────
    this.isOffline.set(!navigator.onLine);
    this.registerOnlineListeners();

    // ── Reanudar el control de inactividad tras una recarga ────────────────
    // AuthService restaura la sesión desde sessionStorage en su propio
    // constructor (antes de que este ngOnInit corra), así que si hay una
    // sesión válida, isAuthenticated() ya es true aquí. Sin esta llamada,
    // SessionService.startTimer() solo se invocaba tras un login
    // interactivo (LoginComponent) — recargar la página con una sesión
    // restaurada dejaba el control de inactividad completamente apagado
    // hasta el siguiente login manual, en vez de simplemente reiniciar el
    // contador (Prompt 19, Fase 0, hallazgo adicional).
    if (this.authService.isAuthenticated()) {
      this.sessionService.startTimer();
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener('offline', this.onOffline);
    window.removeEventListener('online', this.onOnline);
  }

  /** Métodos de event listener guardados como referencias para poder removerlos */
  private readonly onOffline = (): void => {
    this.isOffline.set(true);
  };

  private readonly onOnline = (): void => {
    this.isOffline.set(false);
    // No reintentar automáticamente — el usuario puede hacer clic en "Reintentar"
    // en el componente que falló. Más predecible que un reintento oculto.
  };

  private registerOnlineListeners(): void {
    window.addEventListener('offline', this.onOffline);
    window.addEventListener('online',  this.onOnline);
  }
}
